/**
 * DictationButton — mic toggle that appends final transcripts to a target value.
 *
 * Two modes:
 *  - Controlled: provide `value` + `onChange` (treated like a controlled input).
 *  - Callback:   provide `onTranscript` to receive each final chunk yourself.
 */
import { Mic, MicOff } from 'lucide-react';
import { useSpeechRecognition } from '@/hooks/useSpeechRecognition';
import { cn } from '@/lib/utils/cn';

export interface DictationButtonProps {
  value?: string;
  onChange?: (next: string) => void;
  onTranscript?: (text: string) => void;
  lang?: string;
  /** Visual size. */
  size?: 'sm' | 'md';
  className?: string;
  disabled?: boolean;
  /** Override the button's a11y label. */
  ariaLabel?: string;
}

export function DictationButton({
  value,
  onChange,
  onTranscript,
  lang = 'en-US',
  size = 'md',
  className,
  disabled,
  ariaLabel,
}: DictationButtonProps) {
  const { supported, listening, error, toggle } = useSpeechRecognition({
    lang,
    onFinal: (text) => {
      if (!text) return;
      if (onTranscript) onTranscript(text);
      if (onChange) {
        const sep = value && !/\s$/.test(value) ? ' ' : '';
        onChange((value ?? '') + sep + text);
      }
    },
  });

  if (!supported) return null;

  const px = size === 'sm' ? 'w-7 h-7' : 'w-9 h-9';
  const ix = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={disabled}
      aria-pressed={listening}
      aria-label={ariaLabel ?? (listening ? 'Stop dictation' : 'Start dictation')}
      title={error ?? (listening ? 'Listening… click to stop' : 'Dictate with your voice')}
      className={cn(
        'flex-shrink-0 inline-flex items-center justify-center rounded-full border transition-colors',
        px,
        listening
          ? 'bg-[color-mix(in_srgb,var(--color-error)_15%,transparent)] border-[var(--color-error)] text-[var(--color-error)] animate-pulse'
          : 'bg-[var(--color-bg-tertiary)] border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
        disabled && 'opacity-50 cursor-not-allowed',
        className,
      )}
    >
      {listening
        ? <MicOff className={ix} aria-hidden="true" />
        : <Mic    className={ix} aria-hidden="true" />}
    </button>
  );
}
